import _ from 'lodash';
import vision from "@mediapipe/tasks-vision";
const { ImageSegmenter, FilesetResolver } = vision;
import { isMobile, isSafari } from 'react-device-detect';
import * as StackBlur from "stackblur-canvas";

import {
  VIRTUAL_BACKGROUND,
} from "UTILS/Constants";

/**
 * Virtual Background Filter
 * @version: 2022.2.11
 **/

function hasMediaStream() {
  return typeof MediaStreamTrackProcessor != 'undefined';
}

function hasRequestVideoFrameCallback() {
  return "requestVideoFrameCallback" in HTMLVideoElement.prototype;
}
/**
 * Virtual Background Filter library
 */
export class VirtualBackgroundFilter {
  stream;
  filteredStream;
  config;
  abortController;
  bgImage;
  canvasElement;
  canvasCtx;
  canvasWidth;
  canvasHeight;
  canvasElementBlur;
  canvasCtxBlur;
  sourceVideo;
  videoFrameCallbackID;
  prevTime;
  imageSegmenter;
  smoothingRangeMin;
  smoothingRangeMax;
  smoothingRange;
  blurRadiusPixel;
  errorHandler;
  drawBlurredBackgroundProcess;
  /**
   * @constructor
   * @param  {Object}   config  input parameter
   * @param  {boolean}  config.blur  blur mode
   * @param  {number}   config.blurRadius blur Radius(px)
   * @param  {string}   config.image  image path
   */
  constructor(config) {
    this.stream = undefined;
    this.filteredStream = undefined;
    this.setBackground(config);
    this.abortController = undefined;
    this.bgImage = document.createElement('img');
    this.bgImage.crossOrigin = 'anonymous';
    this.sourceVideo = null;
    this.videoFrameCallbackID = -1;
    this.prevTime = Date.now();
    this.canvasElement = document.createElement('canvas');
    this.canvasElement.style.display = 'none';
    const ctx = this.canvasElement.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      throw Error("getContext('2d') failed");
    }
    this.canvasCtx = ctx;
    this.canvasWidth = 0;
    this.canvasHeight = 0;

    this.canvasElementBlur = document.createElement('canvas');
    this.canvasElementBlur.style.display = 'none';
    const ctx2 = this.canvasElementBlur.getContext('2d', { willReadFrequently: true });
    if (!ctx2) {
      throw Error("getContext('2d') failed");
    }
    this.canvasCtxBlur = ctx2;
    this.imageSegmenter = null;

    this.smoothingRangeMin = 0.55;
    this.smoothingRangeMax = 0.8;
    this.smoothingRange = this.smoothingRangeMax - this.smoothingRangeMin;
    this.blurRadiusPixel = isSafari ? VIRTUAL_BACKGROUND.BLUR_RADIUS_PIXEL_BASE_FOR_SAFARI : VIRTUAL_BACKGROUND.BLUR_RADIUS_PIXEL_BASE;
    this.errorHandler = null;
    this.drawBlurredBackgroundProcess = isSafari ? this.blurProcessSafari : this.blurProcessNotSafari;
  }

  static #imageSegmenterOriginalInstance = null;
  static #currentUrl = location.protocol + '//' + location.host;

  /**
   * Create an instance of Image segmenter
   */
  static #createImageSegmenterInstance = async () => {
    if ( VirtualBackgroundFilter.#imageSegmenterOriginalInstance ) {
      return VirtualBackgroundFilter.#imageSegmenterOriginalInstance;
    }
    try {
      const delegate = "CPU";
      const runningMode = "VIDEO";
      let baseUrl = VirtualBackgroundFilter.#currentUrl;
      // When running in a local environment, change "baseUrl" to a server that publishes materials.
      // baseUrl = "https://dev02.finplex-exclosertest.com";

      const audio = await FilesetResolver.forVisionTasks(baseUrl + "/npm/@mediapipe/tasks-vision@0.10.2/wasm/");
      const imageSegmenter = await ImageSegmenter.createFromOptions(audio, {
        baseOptions: {
          modelAssetPath: baseUrl + "/module/virtualbg/image_segmenter/selfie_segmenter_square.tflite",
          delegate: delegate
        },
        runningMode: runningMode,
        outputCategoryMask: false,
        outputConfidenceMasks: true,
      });
      VirtualBackgroundFilter.#imageSegmenterOriginalInstance = imageSegmenter;
      return imageSegmenter;
    } catch ( error ) {
      throw error;
    }
  }

  /**
   * Draw an image with a virtual background applied to canvasElement
   * @param  {ImageBitmap} imageBitmap  Bitmap image of video frame.
   * @param  {number} timestamp  The timestamp of the current frame, in ms.
   */
  drawVideoImage = async (imageBitmap, timestamp ) => {
    let errorInfo = null;
    await this.imageSegmenter.segmentForVideo(imageBitmap, timestamp, async (result) => {
      try{
        this.canvasCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

        this.canvasCtx.save();

        // == Generates an image with the recognized parts of the person displayed in white. ==
        // imageData = Uint8ClampedArray that holds the RGBA array of ImageData object
        let imageData = this.baseImageData;

        // Get the confidence (0 to 1) whether each pixel is a person from the ImageSegmenter analysis result.
        let confidenceMask = [];
        if ( result.confidenceMasks[0].hasWebGLTexture() || result.confidenceMasks[0].hasFloat32Array()) {
          confidenceMask = result.confidenceMasks[0].getAsFloat32Array();
        }

        // Generates the following RGBA data for each pixel
        //  Pixel confidence  | background or person / color
        //  ----------------------------------------------------------------------
        //  0.55 or less      | background / transparent black  rgba(0,0,0,0)
        //  0.55-0.8          | person / white(The higher the trust, the lower the transparency) rgba(255,255,255,1-255)
        //  0.8 or higher     | person / white without transparency rgba(255,255,255,255)
        // Pay attention to performance due to the large number of loops.
        // It seems that using imported constants may slow down the process.
        let imageDataIdx = 0;
        for (let maskIdx = 0; maskIdx < confidenceMask.length; ++maskIdx) {
          // for selfie_segmenter_square
          if ( confidenceMask[maskIdx] && confidenceMask[maskIdx] > this.smoothingRangeMin ) {
            imageData[imageDataIdx    ] = 255;
            imageData[imageDataIdx + 1] = 255;
            imageData[imageDataIdx + 2] = 255;
            const alpha = confidenceMask[maskIdx] > this.smoothingRangeMax ?
              255 :
              255 * ( ( confidenceMask[maskIdx] - this.smoothingRangeMin ) / this.smoothingRange );
            imageData[imageDataIdx + 3] = alpha;
          } else {
            imageData[imageDataIdx    ] = 0;
            imageData[imageDataIdx + 1] = 0;
            imageData[imageDataIdx + 2] = 0;
            imageData[imageDataIdx + 3] = 0;
          }
          imageDataIdx += 4;
        }
        result.close();
        const dataNew = new ImageData(imageData, this.canvasWidth, this.canvasHeight);
        this.canvasCtx.putImageData(dataNew, 0, 0);

        // == Draw a background on parts that are not recognized as people ==
        this.canvasCtx.globalCompositeOperation = 'source-out';
        if (this.config.blur) { // Blur mode
          this.drawBlurredBackgroundProcess(imageBitmap);
        } else { // background image mode
          this.canvasCtx.drawImage(this.bgImage, 0, 0, this.canvasWidth, this.canvasHeight);
        }
        // == Draw only the people overlapping each other ==
        this.canvasCtx.globalCompositeOperation = 'destination-atop';
        this.canvasCtx.drawImage(imageBitmap, 0, 0, this.canvasWidth, this.canvasHeight);

        this.canvasCtx.restore();
      } catch( error ) {
        errorInfo = error;
      }
    });
    if ( errorInfo ) {
      throw errorInfo;
    }
  }
  /**
   * Blur processing for PC/Android
   * @param  {ImageBitmap} imageBitmap  Bitmap image of video frame.
   */
  blurProcessNotSafari = async (imageBitmap) => {
    this.canvasCtxBlur.drawImage(imageBitmap, 0, 0, this.canvasWidth, this.canvasHeight);
    this.canvasCtx.drawImage(this.canvasElementBlur, 0, 0, this.canvasWidth, this.canvasHeight);
  }
  /**
   * Blur processing for Safari
   * @param  {ImageBitmap} imageBitmap  Bitmap image of video frame.
   */
  blurProcessSafari = async (imageBitmap) => {
    this.canvasCtxBlur.drawImage(imageBitmap, 0, 0, this.canvasWidth, this.canvasHeight);
    StackBlur.canvasRGBA(this.canvasElementBlur, 0, 0, this.canvasWidth, this.canvasHeight, this.blurRadiusPixel);
    this.canvasCtx.drawImage(this.canvasElementBlur, 0, 0, this.canvasWidth, this.canvasHeight);
  }

  /**
   * Update canvas for drawing
   * @param  {Number} width  canvas width
   * @param  {Number} height  canvas height
   */
  updateCanvas = async (width, height) => {
    if (width && height) {
      this.canvasElement.width = width;
      this.canvasElement.height = height;
      this.canvasElementBlur.width = width;
      this.canvasElementBlur.height = height;
      const baseHeight = VIRTUAL_BACKGROUND.VIDEO_HEIGHT_BASE / VIRTUAL_BACKGROUND.RESOLUTION_ADJUSTMENT_VALUE;
      let magnification = height / baseHeight;
      if ( magnification < 1 ) magnification = 1;

      if ( isSafari ) {
        this.blurRadiusPixel = Math.round(magnification * VIRTUAL_BACKGROUND.BLUR_RADIUS_PIXEL_BASE_FOR_SAFARI);
        this.canvasCtxBlur.filter = 'none';
      } else {
        this.blurRadiusPixel = Math.round(magnification * VIRTUAL_BACKGROUND.BLUR_RADIUS_PIXEL_BASE);
        this.canvasCtxBlur.filter = `blur(${this.blurRadiusPixel}px)`;
      }

      this.canvasWidth = this.canvasElement.width;
      this.canvasHeight = this.canvasElement.height;

      this.canvasCtx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
      this.baseImageData = this.canvasCtx.getImageData(0, 0, this.canvasWidth, this.canvasHeight).data;
    }
  }

  /**
   * Initialize MediaStreamProcessor
   * @param  {MediaStream} stream  Original MediaStream
   */
  createMediaStreamProcessor = async (stream) => {
    if ( this.abortController ) {
      this.abortController.abort(`stop filter by createMediaStreamProcessor`);
    }
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    const processor = new MediaStreamTrackProcessor({ track: stream.getVideoTracks()[0] });
    const generator = new MediaStreamTrackGenerator({ kind: 'video' });
    processor.readable.pipeThrough(new TransformStream({
      transform: async (frame, controller) => {
        const settings = stream.getVideoTracks()[0].getSettings();
        if ( this.canvasWidth != settings.width || this.canvasHeight != settings.height ) {
          await this.updateCanvas(settings.width, settings.height);
        }
        const timestamp = performance.now();
        // imageBitmap = ImageBitmap object
        const imageBitmap = await createImageBitmap(frame);
        if (!this.config.blur && !this.config.image) {
          this.canvasCtx.drawImage(imageBitmap, 0, 0, this.canvasWidth, this.canvasHeight);
          imageBitmap.close();
          controller.enqueue(frame);
          return;
        }
        this.drawVideoImage(imageBitmap, timestamp).then(() => {
          controller.enqueue(frame);
        }).catch((error) => {
          console.error(error);
          frame.close();
          this.abortController.abort(`stop filter/internal error in doProcess`);
          if (this.errorHandler) this.errorHandler(error);
        });
      },
    }), { signal }).pipeTo(generator.writable).catch((e) => {
      if (signal.aborted) {
        console.info(`signal.aborted reason:${signal.reason}`);
      } else {
        console.error(e);
        if (this.errorHandler) this.errorHandler(e);
      }
      processor.readable.cancel(e).catch((error) => {
        console.warn(error);
      });
      generator.writable.abort(e).catch((error) => {
        console.warn(error);
      });
      this.abortController = undefined;
    });
  }

  /**
   * Callback used in requestVideoFrameCallback()
   * @param  {DOMHighResTimeStamp} now timestamp of current time
   * @param  {VideoFrameCallbackMetadata} metadata Video frame metadata
   */
  videoFrameCallback = async (now, metadata) => {
    try {
      const imageBitmap = await createImageBitmap(this.sourceVideo);
      if ( this.canvasWidth != imageBitmap.width || this.canvasHeight != imageBitmap.height ) {
        await this.updateCanvas(imageBitmap.width, imageBitmap.height);
      }

      const timestamp = performance.now();
      await this.drawVideoImage(imageBitmap, timestamp).then(()=>{
        this.videoFrameCallbackID = this.sourceVideo.requestVideoFrameCallback(this.videoFrameCallback);
      }).catch((error)=>{
        throw error;
      }).finally(()=> {
        imageBitmap.close();
      });
    } catch( e) {
      console.error(e);
      if (this.errorHandler) this.errorHandler(e);
    }
  }
  /**
   * Set requestVideoFrameCallback
   * @param  {MediaStream} stream  Original MediaStream
   */
  setVideoFrameCallback = async (stream) => {
    const sourceVideo = document.createElement('video');
    sourceVideo.style.visibility = 'hidden';
    sourceVideo.style.display = 'none';
    sourceVideo.muted = true;
    sourceVideo.playsInline = true;

    // Setting video to canvas
    sourceVideo.srcObject = stream;
    await sourceVideo.play().then(()=>{
      const videoFrameCallbackID = sourceVideo.requestVideoFrameCallback(this.videoFrameCallback);
      this.videoFrameCallbackID = videoFrameCallbackID;
    })
    this.sourceVideo = sourceVideo;
  }

  /**
   * Retrieve the filtered Stream
   * @param  {MediaStream} stream  Original MediaStream
   * @param  {ErrorHandler} errorHandler Handler called when an error occurs during drawing processing
   * @return {MediaStream} Filtered MediaStream
   */
  getFilteredStream = async (stream, errorHandler) => {
    try {
      if ( !this.imageSegmenter ) {
        this.imageSegmenter = await VirtualBackgroundFilter.#createImageSegmenterInstance();
      }
      if ( this.stream ) {
        await this.stop();
      }
      this.stream = stream;
      if(errorHandler) {
        this.errorHandler = errorHandler;
      } else {
        this.errorHandler = null;
      }
      const settings = stream.getVideoTracks()[0].getSettings();
      if (isMobile && isSafari && window.orientation == 0) {
        await this.updateCanvas(settings.height, settings.width);
      } else {
        await this.updateCanvas(settings.width, settings.height);
      }
      if (hasMediaStream()) {
        await this.createMediaStreamProcessor(this.stream);
      } else if (hasRequestVideoFrameCallback()) {
        // Settings for iOS
        await this.setVideoFrameCallback(this.stream);
      }

      this.filteredStream = this.canvasElement.captureStream();
      return this.filteredStream;
    } catch ( error ) {
      throw error;
    }
  }

  /**
   * background change
   * @param  {Object}   config  input parameter
   * @param  {boolean}  config.blur  blur mode
   * @param  {number}   config.blurRadius blur Radius(px)
   * @param  {string}   config.image  image path
   */
  setBackground = (config) => {
    if (config.image) {
      const tmpImage = new Image();
      tmpImage.onload = () => {
        this.config = _.cloneDeep(config);
        this.bgImage = tmpImage;
      };
      tmpImage.src = config.image;
    }
    else {
      this.config = _.cloneDeep(config);
    }
  }

  /**
   * Stop filtering
   */
  stop = async () =>{
    if (hasMediaStream() && this.abortController) {
      this.abortController.abort(`stop filter`);
      this.abortController = undefined;
    } else if (hasRequestVideoFrameCallback() && this.sourceVideo) {
      this.sourceVideo.cancelVideoFrameCallback(this.videoFrameCallbackID);
      this.videoFrameCallbackID = -1;
      this.sourceVideo = null;
    }
    if (this.stream && this.filteredStream) {
      this.filteredStream.removeTrack(this.filteredStream.getTracks()[0]);
      this.filteredStream.addTrack(this.stream.getTracks()[0]);
    }
  }
  /**
   * Destroy the MediaStream of the virtual background
   */
  destroy = () => {
    this.stop();
    if (this.filteredStream) {
      this.filteredStream.getTracks().forEach(function (track) {
        track.stop();
      });
    }
    this.filteredStream = undefined;
    this.stream = undefined;
  }
}

